
import { useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

export const useRecaptcha = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      console.error('VITE_RECAPTCHA_SITE_KEY não está configurada no .env');
      setIsLoaded(false);
      return;
    }

    const host = window.location.hostname;
    const allowedHosts = [
      'localhost',
      '127.0.0.1',
      'krecaolanches.vercel.app',
      'www.krecaolanches.vercel.app',
      'krecaolanches.com.br',
      'www.krecaolanches.com.br',
    ];

    const isAllowedDomain = allowedHosts.includes(host);

    // Evita erro "domínio inválido para a chave do site" em domínios não permitidos
    if (!isAllowedDomain) {
      console.warn('Domínio não autorizado para reCAPTCHA; não carregando o script');
      setIsLoaded(false);
      return;
    }

    // Verificar se o script já foi carregado
    if (window.grecaptcha) {
      setIsLoaded(true);
      return;
    }

    // Carregar o script do reCAPTCHA
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setIsLoaded(true);
          console.log('reCAPTCHA carregado com sucesso');
        });
      }
    };

    script.onerror = () => {
      console.error('Erro ao carregar o script do reCAPTCHA');
      setIsLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      // Limpar o script quando o componente for desmontado
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!RECAPTCHA_SITE_KEY) {
      console.error('VITE_RECAPTCHA_SITE_KEY não está configurada');
      return null;
    }

    if (!isLoaded || !window.grecaptcha) {
      console.warn('reCAPTCHA não está carregado');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      return token;
    } catch (error) {
      console.error('Erro ao executar reCAPTCHA:', error);
      return null;
    }
  };

  return {
    isLoaded,
    executeRecaptcha
  };
};
